import React, { useState, useEffect } from 'react';
import Styles from './Step6SocialChannels.module.scss';
import {
  FiCheck,
  FiInstagram,
  FiLinkedin,
  FiTwitter,
  FiYoutube,
  FiFacebook,
  FiDownload,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiPlay,
  FiBarChart2,
  FiRefreshCw,
  FiZap,
  FiTrendingUp,
  FiTarget,
  FiInfo,
  FiTrash2,
} from 'react-icons/fi';
import DocumentHealthTooltip from 'Containers/Editor/Components/Document Health/DocumentHealthTooltip';
import { FaTiktok } from 'react-icons/fa';
import { postRequest, getRequest, deleteRequest } from 'helpers/axios';
import { success as successToast, error as errorToast } from 'helpers/toaster';
import ConfirmDeleteModal from 'Containers/Modal/ConfirmationModal/ConfirmationModal';

const SOCIAL_PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: FiInstagram, color: '#E4405F' },
  { id: 'linkedin', name: 'LinkedIn', icon: FiLinkedin, color: '#0A66C2' },
  { id: 'twitter', name: 'Twitter/X', icon: FiTwitter, color: '#1DA1F2' },
  { id: 'facebook', name: 'Facebook', icon: FiFacebook, color: '#1877F2', disabled: true },
  { id: 'youtube', name: 'YouTube', icon: FiYoutube, color: '#FF0000' },
  { id: 'tiktok', name: 'TikTok', icon: FaTiktok, color: '#000000' },
];

const Step6SocialChannels = ({ data, brandId: propBrandId, onUpdate, onNext, onPrevious, onSaveAndExit, onSaveAndContinue }) => {
  const { socialChannels = [], postingFrequency = '', contentSamples = [] } = data;

  const [brandId, setBrandId] = useState(propBrandId);

  // Sync local brandId state when prop changes
  React.useEffect(() => {
    if (propBrandId) {
      setBrandId(propBrandId);
    }
  }, [propBrandId]);

  const [channelUrls, setChannelUrls] = useState({});
  const [channelPurposes, setChannelPurposes] = useState({});
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [platformToRemove, setPlatformToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Sync local state when socialChannels prop changes (e.g., after API load)
  useEffect(() => {
    if (socialChannels.length === 0) return;

    const newUrls = {};
    const newPurposes = {};

    socialChannels.forEach(channel => {
      if (channel.url) {
        newUrls[channel.platform] = channel.url;
      }
      if (channel.purpose) {
        newPurposes[channel.platform] = channel.purpose;
      }
    });

    // Merge with existing state to preserve user edits
    setChannelUrls(prev => ({ ...prev, ...newUrls }));
    setChannelPurposes(prev => ({ ...prev, ...newPurposes }));
  }, [socialChannels]);

  const [fetchedTweets, setFetchedTweets] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [frequencyStats, setFrequencyStats] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Active tab for channel details
  const [activeTab, setActiveTab] = useState(null);

  // Platform-specific states
  const [fetchedInstagramPosts, setFetchedInstagramPosts] = useState([]);
  const [instagramProfile, setInstagramProfile] = useState(null);
  const [instagramFrequencyStats, setInstagramFrequencyStats] = useState(null);
  const [isFetchingInstagram, setIsFetchingInstagram] = useState(false);

  const [fetchedLinkedInPosts, setFetchedLinkedInPosts] = useState([]);
  const [linkedInProfile, setLinkedInProfile] = useState(null);
  const [linkedInFrequencyStats, setLinkedInFrequencyStats] = useState(null);
  const [isFetchingLinkedIn, setIsFetchingLinkedIn] = useState(false);

  const [fetchedYouTubeVideos, setFetchedYouTubeVideos] = useState([]);
  const [youTubeProfile, setYouTubeProfile] = useState(null);
  const [youTubeFrequencyStats, setYouTubeFrequencyStats] = useState(null);
  const [isFetchingYouTube, setIsFetchingYouTube] = useState(false);

  const [fetchedTikTokVideos, setFetchedTikTokVideos] = useState([]);
  const [tikTokProfile, setTikTokProfile] = useState(null);
  const [tikTokFrequencyStats, setTikTokFrequencyStats] = useState(null);
  const [isFetchingTikTok, setIsFetchingTikTok] = useState(false);

  // Collapsible posts state
  const [expandedPosts, setExpandedPosts] = useState({});
  // Show all posts per platform
  const [showAllPosts, setShowAllPosts] = useState({});

  // Content insights analysis state
  const [contentInsights, setContentInsights] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState({});
  const [expandedInsights, setExpandedInsights] = useState({});

  // Derived state - selected platforms
  const selectedPlatforms = socialChannels.map((ch) => ch.platform);

  // Set first selected platform as active tab
  useEffect(() => {
    if (selectedPlatforms.length > 0 && !activeTab) {
      setActiveTab(selectedPlatforms[0]);
    } else if (selectedPlatforms.length === 0) {
      setActiveTab(null);
    } else if (activeTab && !selectedPlatforms.includes(activeTab)) {
      setActiveTab(selectedPlatforms[0] || null);
    }
  }, [selectedPlatforms.length, activeTab]);

  // Load previously fetched data from saved social channels
  useEffect(() => {
    // Track URLs to prepopulate from saved profile data
    const urlUpdates = {};

    // Load Twitter data
    const twitterChannel = socialChannels.find(ch => ch.platform === 'twitter');
    if (twitterChannel?.platform_specific_settings) {
      const settings = twitterChannel.platform_specific_settings;
      if (settings.user_profile) {
        setUserProfile(settings.user_profile);
        // Prepopulate URL from profile username if not already set
        if (!channelUrls.twitter && settings.user_profile.username) {
          urlUpdates.twitter = `@${settings.user_profile.username}`;
        }
      }
      if (settings.frequency_stats) {
        setFrequencyStats(settings.frequency_stats);
      }
      if (settings.fetched_posts && settings.fetched_posts.length > 0) {
        setFetchedTweets(settings.fetched_posts);
      }
    }

    // Load Instagram data
    const instagramChannel = socialChannels.find(ch => ch.platform === 'instagram');
    if (instagramChannel?.platform_specific_settings) {
      const settings = instagramChannel.platform_specific_settings;
      if (settings.user_profile) {
        setInstagramProfile(settings.user_profile);
        // Prepopulate URL from profile username if not already set
        if (!channelUrls.instagram && settings.user_profile.username) {
          urlUpdates.instagram = `@${settings.user_profile.username}`;
        }
      }
      if (settings.frequency_stats) {
        setInstagramFrequencyStats(settings.frequency_stats);
      }
      if (settings.fetched_posts && settings.fetched_posts.length > 0) {
        setFetchedInstagramPosts(settings.fetched_posts);
      }
    }

    // Load LinkedIn data
    const linkedInChannel = socialChannels.find(ch => ch.platform === 'linkedin');
    if (linkedInChannel?.platform_specific_settings) {
      const settings = linkedInChannel.platform_specific_settings;
      if (settings.user_profile) {
        setLinkedInProfile(settings.user_profile);
        // Prepopulate URL from profile if not already set
        if (!channelUrls.linkedin && (settings.user_profile.linkedin_url || settings.profile_url)) {
          urlUpdates.linkedin = settings.user_profile.linkedin_url || settings.profile_url;
        }
      }
      if (settings.frequency_stats) {
        setLinkedInFrequencyStats(settings.frequency_stats);
      }
      if (settings.fetched_posts && settings.fetched_posts.length > 0) {
        setFetchedLinkedInPosts(settings.fetched_posts);
      }
    }

    // Load YouTube data
    const youTubeChannel = socialChannels.find(ch => ch.platform === 'youtube');
    if (youTubeChannel?.platform_specific_settings) {
      const settings = youTubeChannel.platform_specific_settings;
      // Backend stores channel data in 'channel_data', not 'user_profile'
      const channelData = settings.channel_data || settings.user_profile;
      if (channelData) {
        setYouTubeProfile(channelData);
        // Prepopulate URL from profile if not already set
        if (!channelUrls.youtube && (settings.profile_url || channelData.custom_url)) {
          urlUpdates.youtube = settings.profile_url || `https://youtube.com/${channelData.custom_url}`;
        }
      }
      if (settings.frequency_stats) {
        setYouTubeFrequencyStats(settings.frequency_stats);
      }
      if (settings.fetched_posts && settings.fetched_posts.length > 0) {
        setFetchedYouTubeVideos(settings.fetched_posts);
      }
    }

    // Load TikTok data
    const tikTokChannel = socialChannels.find(ch => ch.platform === 'tiktok');
    if (tikTokChannel?.platform_specific_settings) {
      const settings = tikTokChannel.platform_specific_settings;
      if (settings.user_profile) {
        setTikTokProfile(settings.user_profile);
        // Prepopulate URL from profile username if not already set
        if (!channelUrls.tiktok && settings.user_profile.username) {
          urlUpdates.tiktok = `@${settings.user_profile.username}`;
        }
      }
      if (settings.frequency_stats) {
        setTikTokFrequencyStats(settings.frequency_stats);
      }
      if (settings.fetched_posts && settings.fetched_posts.length > 0) {
        setFetchedTikTokVideos(settings.fetched_posts);
      }
    }

    // Update channel URLs if we found any from saved profiles
    if (Object.keys(urlUpdates).length > 0) {
      setChannelUrls(prev => ({ ...prev, ...urlUpdates }));
    }

    // Load posts from content samples (fallback for each platform - ONLY if no fetched_posts in settings)
    // Twitter
    const twitterSamples = contentSamples.filter(s => s.platform === 'twitter' && s.content_type === 'social_post');
    if (twitterSamples.length > 0 && !twitterChannel?.platform_specific_settings?.fetched_posts?.length) {
      const tweets = twitterSamples.map(s => ({
        id: s.id,
        text: s.content || s.title,
        created_at: s.metadata?.created_at || s.created_at,
        likes: s.metadata?.engagement?.likes || s.metadata?.likes || 0,
        retweets: s.metadata?.engagement?.retweets || s.metadata?.retweets || 0,
        replies: s.metadata?.engagement?.replies || s.metadata?.replies || 0,
        url: s.metadata?.url || s.url,
        hashtags: s.tags || []
      }));
      setFetchedTweets(tweets);
    }

    // Instagram (includes reels saved as video_script)
    const instagramSamples = contentSamples.filter(s => s.platform === 'instagram' && (s.content_type === 'social_post' || s.content_type === 'video_script'));
    if (instagramSamples.length > 0 && !instagramChannel?.platform_specific_settings?.fetched_posts?.length) {
      const posts = instagramSamples.map(s => ({
        id: s.id,
        caption: s.content || s.title,
        created_at: s.metadata?.created_at || s.created_at,
        likes: s.metadata?.engagement?.likes || s.metadata?.likes || 0,
        comments: s.metadata?.engagement?.comments || s.metadata?.comments || 0,
        thumbnail: s.metadata?.media?.thumbnail_url || s.metadata?.media?.stored_thumbnail_url || s.metadata?.thumbnail_url || s.metadata?.stored_thumbnail_url,
        url: s.metadata?.url || s.source_url,
        type: s.metadata?.post_type || 'post',
      }));
      setFetchedInstagramPosts(posts);
    }

    // LinkedIn
    const linkedInSamples = contentSamples.filter(s => s.platform === 'linkedin' && s.content_type === 'social_post');
    if (linkedInSamples.length > 0 && !linkedInChannel?.platform_specific_settings?.fetched_posts?.length) {
      const posts = linkedInSamples.map(s => ({
        id: s.id,
        text: s.content || s.title,
        created_at: s.metadata?.created_at || s.created_at,
        likes: s.metadata?.engagement?.likes || s.metadata?.likes || 0,
        comments: s.metadata?.engagement?.comments || s.metadata?.comments || 0,
        reposts: s.metadata?.engagement?.reposts || 0,
        url: s.metadata?.url || s.url,
      }));
      setFetchedLinkedInPosts(posts);
    }

    // YouTube
    const youtubeSamples = contentSamples.filter(s => s.platform === 'youtube' && (s.content_type === 'social_post' || s.content_type === 'youtube_video' || s.content_type === 'youtube_short'));
    if (youtubeSamples.length > 0 && !youTubeChannel?.platform_specific_settings?.fetched_posts?.length) {
      const videos = youtubeSamples.map(s => ({
        id: s.id,
        title: s.title || s.content,
        description: s.content,
        created_at: s.created_at,
        views: s.metadata?.engagement?.views || s.metadata?.views || 0,
        likes: s.metadata?.engagement?.likes || s.metadata?.likes || 0,
        comments: s.metadata?.engagement?.comments || s.metadata?.comments || 0,
        stored_thumbnail_url: s.metadata?.stored_thumbnail_url,
        thumbnail_url: s.metadata?.thumbnail_url || s.metadata?.thumbnail || s.metadata?.image_url,
        thumbnail: s.metadata?.thumbnail_url || s.metadata?.thumbnail || s.metadata?.image_url,
        url: s.metadata?.url || s.source_url,
      }));
      setFetchedYouTubeVideos(videos);
    }

    // TikTok
    const tiktokSamples = contentSamples.filter(s => s.platform === 'tiktok' && (s.content_type === 'social_post' || s.content_type === 'tiktok_video'));
    if (tiktokSamples.length > 0 && !tikTokChannel?.platform_specific_settings?.fetched_posts?.length) {
      const videos = tiktokSamples.map(s => ({
        id: s.id,
        title: s.title || s.content,
        description: s.content,
        created_at: s.created_at,
        views: s.metadata?.engagement?.views || s.metadata?.views || 0,
        likes: s.metadata?.engagement?.likes || s.metadata?.likes || 0,
        comments: s.metadata?.engagement?.comments || s.metadata?.comments || 0,
        stored_thumbnail_url: s.metadata?.stored_thumbnail_url,
        thumbnail_url: s.metadata?.thumbnail_url || s.metadata?.thumbnail || s.metadata?.image_url,
        thumbnail: s.metadata?.thumbnail_url || s.metadata?.thumbnail || s.metadata?.image_url,
        url: s.metadata?.url || s.source_url,
      }));
      setFetchedTikTokVideos(videos);
    }
  }, [socialChannels, contentSamples]);

  const togglePlatform = (platformId) => {
    if (selectedPlatforms.includes(platformId)) {
      // Remove platform
      onUpdate({
        socialChannels: socialChannels.filter((ch) => ch.platform !== platformId),
      });
      const newUrls = { ...channelUrls };
      const newPurposes = { ...channelPurposes };
      delete newUrls[platformId];
      delete newPurposes[platformId];
      setChannelUrls(newUrls);
      setChannelPurposes(newPurposes);
    } else {
      // Add platform
      onUpdate({
        socialChannels: [
          ...socialChannels,
          { platform: platformId, url: '', purpose: '' },
        ],
      });
    }
  };

  const handleUrlChange = (platformId, url) => {
    setChannelUrls({ ...channelUrls, [platformId]: url });
    onUpdate({
      socialChannels: socialChannels.map((ch) =>
        ch.platform === platformId ? { ...ch, url } : ch
      ),
    });
  };

  const handlePurposeChange = (platformId, purpose) => {
    setChannelPurposes({ ...channelPurposes, [platformId]: purpose });
    onUpdate({
      socialChannels: socialChannels.map((ch) =>
        ch.platform === platformId ? { ...ch, purpose } : ch
      ),
    });
  };

  const handleRemoveChannel = async () => {
    if (!brandId || !platformToRemove) return;
    setIsRemoving(true);
    try {
      const response = await deleteRequest(
        `brand_profiles/${brandId}/brand_intelligence/social_channels/${platformToRemove}`
      );
      if (response.data.success) {
        const platformName = SOCIAL_PLATFORMS.find(p => p.id === platformToRemove)?.name || platformToRemove;
        successToast(`${platformName} channel and ${response.data.removed_samples || 0} content samples removed`);
        // Clear local URL and purpose state
        const newUrls = { ...channelUrls };
        const newPurposes = { ...channelPurposes };
        delete newUrls[platformToRemove];
        delete newPurposes[platformToRemove];
        setChannelUrls(newUrls);
        setChannelPurposes(newPurposes);
        // Clear platform-specific fetched data and profile
        if (platformToRemove === 'twitter') { setFetchedTweets([]); }
        if (platformToRemove === 'instagram') { setFetchedInstagramPosts([]); setInstagramProfile(null); }
        if (platformToRemove === 'linkedin') { setFetchedLinkedInPosts([]); setLinkedInProfile(null); }
        if (platformToRemove === 'youtube') { setFetchedYouTubeVideos([]); setYouTubeProfile(null); }
        if (platformToRemove === 'tiktok') { setFetchedTikTokVideos([]); setTikTokProfile(null); }
        // Remove channel from parent state and filter out content samples for this platform
        onUpdate({
          socialChannels: socialChannels.filter(ch => ch.platform !== platformToRemove),
          contentSamples: (data.contentSamples || []).filter(s => s.platform !== platformToRemove),
        });
      } else {
        errorToast(response.data.error || 'Failed to remove channel');
      }
    } catch (error) {
      console.error('Remove channel error:', error);
      errorToast('Failed to remove channel');
    } finally {
      setIsRemoving(false);
      setShowRemoveModal(false);
      setPlatformToRemove(null);
    }
  };

  const handleContinue = () => {
    // Pass current data for consistency (social channels saved via separate APIs)
    if (onSaveAndContinue) {
      onSaveAndContinue(data);
    } else {
      onNext();
    }
  };

  const extractUsername = (url) => {
    if (!url) return '';
    // Remove @ if present
    url = url.trim().replace(/^@/, '');
    // Extract from URL
    if (url.match(/twitter\.com|x\.com/)) {
      try {
        const urlObj = new URL(url);
        const username = urlObj.pathname.split('/')[1];
        return username || url;
      } catch {
        return url;
      }
    }
    return url;
  };

  // Analyze content for pillars, top performers, and insights
  const analyzeContentInsights = async (platform, forceRefresh = false) => {
    if (!brandId) {
      console.log('No brandId, skipping content analysis');
      return;
    }

    // Check for cached insights unless forcing refresh
    if (!forceRefresh && contentInsights[platform]) {
      console.log(`Using cached insights for ${platform}`);
      return;
    }

    setIsAnalyzing(prev => ({ ...prev, [platform]: true }));

    try {
      const response = await postRequest(
        `brand_profiles/${brandId}/brand_intelligence/analyze_social_insights`,
        { platform, force_refresh: forceRefresh }
      );

      if (response.data.success) {
        setContentInsights(prev => ({
          ...prev,
          [platform]: response.data.data
        }));

        // Auto-expand if this is first analysis
        if (!contentInsights[platform]) {
          setExpandedInsights(prev => ({ ...prev, [platform]: true }));
        }

        if (!response.data.cached) {
          successToast(`Content analysis complete for ${platform}`);
        }
      }
    } catch (error) {
      console.error('Error analyzing content:', error);
      // Don't show error toast - analysis is optional
    } finally {
      setIsAnalyzing(prev => ({ ...prev, [platform]: false }));
    }
  };

  // Toggle insights section visibility
  const toggleInsights = (platform) => {
    setExpandedInsights(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  const handleFetchTwitterPosts = async () => {
    const twitterChannel = socialChannels.find((ch) => ch.platform === 'twitter');
    if (!twitterChannel || !twitterChannel.url) {
      errorToast('Please enter a Twitter username or URL first');
      return;
    }

    const username = extractUsername(twitterChannel.url);
    if (!username) {
      errorToast('Invalid Twitter username or URL');
      return;
    }

    setIsFetching(true);
    setFetchedTweets([]);

    try {
      const requestData = {
        username,
        limit: 20,
        exclude_retweets: true,
        exclude: ['retweets', 'replies'] // Get original tweets only
      };

      // Only include brandId if it exists (will be null until Step 7 completion)
      if (brandId) {
        requestData.brand_profile_id = brandId;
      }

      const response = await postRequest('social_posts/fetch_twitter', requestData);

      if (response.data.success) {
        const tweets = response.data.data.tweets;
        const freqStats = response.data.data.frequency_stats;
        const profile = response.data.data.user_profile;

        setFetchedTweets(tweets);
        setFrequencyStats(freqStats);
        setUserProfile(profile);

        // Persist to socialChannels
        const updatedChannels = socialChannels.map((ch) =>
          ch.platform === 'twitter'
            ? {
                ...ch,
                platform_specific_settings: {
                  ...ch.platform_specific_settings,
                  user_profile: profile,
                  frequency_stats: freqStats,
                  fetched_posts: tweets,
                },
              }
            : ch
        );
        onUpdate({ socialChannels: updatedChannels });

        const savedCount = response.data.data.saved_count || 0;
        const totalCount = response.data.data.count;

        if (brandId && savedCount > 0) {
          successToast(
            `Successfully fetched ${totalCount} tweets from @${response.data.data.username}. ${savedCount} saved for AI training.`
          );
        } else {
          successToast(
            `Successfully fetched ${totalCount} tweets from @${response.data.data.username}.`
          );
        }

        // Auto-trigger content analysis after successful fetch
        analyzeContentInsights('twitter');
      } else {
        errorToast(response.data.error || 'Failed to fetch tweets');
      }
    } catch (error) {
      console.error('Error fetching tweets:', error);
      errorToast(
        error.response?.data?.error || 'Failed to fetch tweets. Please try again.'
      );
    } finally {
      setIsFetching(false);
    }
  };

  // Instagram fetch handler
  const handleFetchInstagramPosts = async () => {
    const instagramChannel = socialChannels.find((ch) => ch.platform === 'instagram');
    if (!instagramChannel || !instagramChannel.url) {
      errorToast('Please enter an Instagram username or URL first');
      return;
    }

    const username = extractUsernameGeneric(instagramChannel.url, 'instagram');
    if (!username) {
      errorToast('Invalid Instagram username or URL');
      return;
    }

    setIsFetchingInstagram(true);
    setFetchedInstagramPosts([]);

    try {
      const requestData = {
        username,
        limit: 20,
      };

      if (brandId) {
        requestData.brand_profile_id = brandId;
      }

      const response = await postRequest('social_posts/fetch_instagram', requestData);

      if (response.data.success) {
        const posts = response.data.data.posts || [];
        const freqStats = response.data.data.frequency_stats;
        // API returns 'profile' not 'user_profile'
        const profile = response.data.data.profile || response.data.data.user_profile;

        setFetchedInstagramPosts(posts);
        setInstagramFrequencyStats(freqStats);
        setInstagramProfile(profile);

        // Persist to socialChannels
        const updatedChannels = socialChannels.map((ch) =>
          ch.platform === 'instagram'
            ? {
                ...ch,
                platform_specific_settings: {
                  ...ch.platform_specific_settings,
                  user_profile: profile,
                  frequency_stats: freqStats,
                  fetched_posts: posts,
                },
              }
            : ch
        );
        onUpdate({ socialChannels: updatedChannels });

        successToast(`Successfully fetched ${posts.length} posts from @${username}`);

        // Auto-trigger content analysis after successful fetch
        analyzeContentInsights('instagram');
      } else {
        errorToast(response.data.error || 'Failed to fetch Instagram posts');
      }
    } catch (error) {
      console.error('Error fetching Instagram posts:', error);
      errorToast(error.response?.data?.error || 'Failed to fetch Instagram posts. Please try again.');
    } finally {
      setIsFetchingInstagram(false);
    }
  };

  // LinkedIn fetch handler
  const handleFetchLinkedInPosts = async () => {
    const linkedInChannel = socialChannels.find((ch) => ch.platform === 'linkedin');
    if (!linkedInChannel || !linkedInChannel.url) {
      errorToast('Please enter a LinkedIn profile URL first');
      return;
    }

    setIsFetchingLinkedIn(true);
    setFetchedLinkedInPosts([]);

    try {
      const requestData = {
        profile_url: linkedInChannel.url,
        limit: 20,
      };

      if (brandId) {
        requestData.brand_profile_id = brandId;
      }

      const response = await postRequest('social_posts/fetch_linkedin', requestData);

      if (response.data.success) {
        const posts = response.data.data.posts || [];
        const freqStats = response.data.data.frequency_stats;
        // API returns 'profile' not 'user_profile'
        const profile = response.data.data.profile || response.data.data.user_profile;

        console.log('LinkedIn fetch response:', {
          postsCount: posts.length,
          freqStats: freqStats?.sample_size,
          profile: profile?.name || profile?.full_name
        });

        setFetchedLinkedInPosts(posts);
        setLinkedInFrequencyStats(freqStats);
        setLinkedInProfile(profile);

        // Persist to socialChannels
        const updatedChannels = socialChannels.map((ch) =>
          ch.platform === 'linkedin'
            ? {
                ...ch,
                platform_specific_settings: {
                  ...ch.platform_specific_settings,
                  user_profile: profile,
                  frequency_stats: freqStats,
                  fetched_posts: posts,
                },
              }
            : ch
        );
        onUpdate({ socialChannels: updatedChannels });

        successToast(`Successfully fetched ${posts.length} posts from LinkedIn`);

        // Auto-trigger content analysis after successful fetch
        analyzeContentInsights('linkedin');
      } else {
        errorToast(response.data.error || 'Failed to fetch LinkedIn posts');
      }
    } catch (error) {
      console.error('Error fetching LinkedIn posts:', error);
      errorToast(error.response?.data?.error || 'Failed to fetch LinkedIn posts. Please try again.');
    } finally {
      setIsFetchingLinkedIn(false);
    }
  };

  // YouTube fetch handler
  const handleFetchYouTubeVideos = async () => {
    const youTubeChannel = socialChannels.find((ch) => ch.platform === 'youtube');
    if (!youTubeChannel || !youTubeChannel.url) {
      errorToast('Please enter a YouTube channel URL first');
      return;
    }

    setIsFetchingYouTube(true);
    setFetchedYouTubeVideos([]);

    try {
      const requestData = {
        channel_url: youTubeChannel.url,
        limit: 20,
      };

      if (brandId) {
        requestData.brand_profile_id = brandId;
      }

      const response = await postRequest('social_posts/fetch_youtube', requestData);

      if (response.data.success) {
        const videos = response.data.data.videos || [];
        const freqStats = response.data.data.frequency_stats;
        // API returns 'channel' not 'channel_info'
        const profile = response.data.data.channel || response.data.data.channel_info;

        setFetchedYouTubeVideos(videos);
        setYouTubeFrequencyStats(freqStats);
        setYouTubeProfile(profile);

        // Persist to socialChannels
        const updatedChannels = socialChannels.map((ch) =>
          ch.platform === 'youtube'
            ? {
                ...ch,
                platform_specific_settings: {
                  ...ch.platform_specific_settings,
                  user_profile: profile,
                  frequency_stats: freqStats,
                  fetched_posts: videos,
                },
              }
            : ch
        );
        onUpdate({ socialChannels: updatedChannels });

        successToast(`Successfully fetched ${videos.length} videos from YouTube`);

        // Auto-trigger content analysis after successful fetch
        analyzeContentInsights('youtube');
      } else {
        errorToast(response.data.error || 'Failed to fetch YouTube videos');
      }
    } catch (error) {
      console.error('Error fetching YouTube videos:', error);
      errorToast(error.response?.data?.error || 'Failed to fetch YouTube videos. Please try again.');
    } finally {
      setIsFetchingYouTube(false);
    }
  };

  // TikTok fetch handler
  const handleFetchTikTokVideos = async () => {
    const tikTokChannel = socialChannels.find((ch) => ch.platform === 'tiktok');
    if (!tikTokChannel || !tikTokChannel.url) {
      errorToast('Please enter a TikTok username or URL first');
      return;
    }

    const username = extractUsernameGeneric(tikTokChannel.url, 'tiktok');
    if (!username) {
      errorToast('Invalid TikTok username or URL');
      return;
    }

    setIsFetchingTikTok(true);
    setFetchedTikTokVideos([]);

    try {
      const requestData = {
        username,
        limit: 20,
      };

      if (brandId) {
        requestData.brand_profile_id = brandId;
      }

      const response = await postRequest('social_posts/fetch_tiktok', requestData);

      if (response.data.success) {
        const videos = response.data.data.videos || [];
        const freqStats = response.data.data.frequency_stats;
        // API returns 'profile' not 'user_profile'
        const profile = response.data.data.profile || response.data.data.user_profile;

        setFetchedTikTokVideos(videos);
        setTikTokFrequencyStats(freqStats);
        setTikTokProfile(profile);

        // Persist to socialChannels
        const updatedChannels = socialChannels.map((ch) =>
          ch.platform === 'tiktok'
            ? {
                ...ch,
                platform_specific_settings: {
                  ...ch.platform_specific_settings,
                  user_profile: profile,
                  frequency_stats: freqStats,
                  fetched_posts: videos,
                },
              }
            : ch
        );
        onUpdate({ socialChannels: updatedChannels });

        successToast(`Successfully fetched ${videos.length} videos from @${username}`);

        // Auto-trigger content analysis after successful fetch
        analyzeContentInsights('tiktok');
      } else {
        errorToast(response.data.error || 'Failed to fetch TikTok videos');
      }
    } catch (error) {
      console.error('Error fetching TikTok videos:', error);
      errorToast(error.response?.data?.error || 'Failed to fetch TikTok videos. Please try again.');
    } finally {
      setIsFetchingTikTok(false);
    }
  };

  // Generic username extractor
  const extractUsernameGeneric = (url, platform) => {
    if (!url) return '';
    url = url.trim().replace(/^@/, '');

    const patterns = {
      instagram: /instagram\.com\/([^/?]+)/,
      tiktok: /tiktok\.com\/@?([^/?]+)/,
      linkedin: /linkedin\.com\/in\/([^/?]+)/,
    };

    if (patterns[platform]) {
      const match = url.match(patterns[platform]);
      if (match) return match[1];
    }

    // If no URL pattern matched, assume it's just a username
    return url.split('/').pop().replace('@', '');
  };

  // Toggle post expansion
  const togglePostExpanded = (postId) => {
    setExpandedPosts((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  // Get platform data helper
  const getPlatformData = (platformId) => {
    switch (platformId) {
      case 'twitter':
        return {
          posts: fetchedTweets,
          profile: userProfile,
          frequencyStats: frequencyStats,
          isFetching: isFetching,
          handleFetch: handleFetchTwitterPosts,
        };
      case 'instagram':
        return {
          posts: fetchedInstagramPosts,
          profile: instagramProfile,
          frequencyStats: instagramFrequencyStats,
          isFetching: isFetchingInstagram,
          handleFetch: handleFetchInstagramPosts,
        };
      case 'linkedin':
        return {
          posts: fetchedLinkedInPosts,
          profile: linkedInProfile,
          frequencyStats: linkedInFrequencyStats,
          isFetching: isFetchingLinkedIn,
          handleFetch: handleFetchLinkedInPosts,
        };
      case 'youtube':
        return {
          posts: fetchedYouTubeVideos,
          profile: youTubeProfile,
          frequencyStats: youTubeFrequencyStats,
          isFetching: isFetchingYouTube,
          handleFetch: handleFetchYouTubeVideos,
        };
      case 'tiktok':
        return {
          posts: fetchedTikTokVideos,
          profile: tikTokProfile,
          frequencyStats: tikTokFrequencyStats,
          isFetching: isFetchingTikTok,
          handleFetch: handleFetchTikTokVideos,
        };
      default:
        return { posts: [], profile: null, frequencyStats: null, isFetching: false, handleFetch: () => {} };
    }
  };

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <h2 className={Styles.title}>Social Channels</h2>
        <p className={Styles.subtitle}>
          Configure your social media presence for platform-specific content
        </p>
      </div>

      {/* Form Content */}
      <div className={Styles.formContent}>
        {/* Platform Selection */}
        <div className={Styles.section}>
          <label className={Styles.label}>Select Your Platforms</label>
          <p className={Styles.helpText}>
            Choose the social platforms where you're active
          </p>
          <div className={Styles.platformGrid}>
            {SOCIAL_PLATFORMS.map((platform) => {
              const Icon = platform.icon;
              const isSelected = selectedPlatforms.includes(platform.id);
              const isDisabled = platform.disabled;

              return (
                <div
                  key={platform.id}
                  data-platform={platform.id}
                  className={`${Styles.platformCard} ${isSelected ? Styles.selected : ''} ${isDisabled ? Styles.disabled : ''}`}
                  onClick={() => !isDisabled && togglePlatform(platform.id)}
                  style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <div className={Styles.platformContent}>
                    <div
                      className={`${Styles.iconWrapper} ${isSelected ? Styles.iconActive : ''}`}
                    >
                      <Icon className={Styles.icon} />
                    </div>
                    <div className={Styles.platformName}>
                      {platform.name}
                      {isDisabled && <span style={{ fontSize: '10px', display: 'block', color: '#888' }}>Coming Soon</span>}
                    </div>
                  </div>
                  {isSelected && (
                    <div className={Styles.selectedBadge}>
                      <FiCheck />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Social Presence Summary */}
        {selectedPlatforms.length > 0 && (
          <div className={Styles.section}>
            <label className={Styles.label}>Social Presence Summary</label>
            <div className={Styles.summaryTable}>
              <div className={Styles.summaryHeader}>
                <div className={Styles.summaryCol}>Platform</div>
                <div className={Styles.summaryCol}>Profile</div>
                <div className={Styles.summaryCol}>Followers</div>
                <div className={Styles.summaryCol}>Fetched</div>
                <div className={Styles.summaryCol}>Posts/Week</div>
                <div className={Styles.summaryCol}>
                  Engagement
                  <DocumentHealthTooltip
                    content="Engagement Rate = (Avg. Likes + Comments + Shares per post) ÷ Followers × 100"
                    placement="top"
                  >
                    <FiInfo style={{ marginLeft: '4px', opacity: 0.6, fontSize: '12px' }} />
                  </DocumentHealthTooltip>
                </div>
              </div>
              {selectedPlatforms.map((platformId) => {
                const platform = SOCIAL_PLATFORMS.find((p) => p.id === platformId);
                const Icon = platform?.icon;
                const platformData = getPlatformData(platformId);
                const profile = platformData.profile;
                const freqStats = platformData.frequencyStats;
                const postCount = platformData.posts?.length || freqStats?.sample_size || 0;

                // Calculate engagement rate
                const getEngagement = () => {
                  if (freqStats?.engagement_rate) return `${freqStats.engagement_rate}%`;
                  if (freqStats?.average_engagement) return `${freqStats.average_engagement}%`;
                  const posts = platformData.posts || [];
                  if (posts.length === 0) return '-';
                  // Use same paths as the followers column display
                  const followers = profile?.metrics?.followers ||
                    profile?.metrics?.follower_count ||
                    profile?.follower_count ||
                    profile?.subscriber_count ||
                    profile?.followers ||
                    profile?.followers_count ||
                    0;
                  if (followers === 0) return '-';
                  const totalEngagement = posts.reduce((sum, p) => {
                    return sum + (p.likes || 0) + (p.comments || 0) + (p.retweets || 0) + (p.shares || 0);
                  }, 0);
                  const avgEngagement = totalEngagement / posts.length;
                  const rate = ((avgEngagement / followers) * 100).toFixed(2);
                  return rate > 0 ? `${rate}%` : '-';
                };

                return (
                  <div
                    key={platformId}
                    className={`${Styles.summaryRow} ${activeTab === platformId ? Styles.activeRow : ''}`}
                    onClick={() => setActiveTab(platformId)}
                  >
                    <div className={Styles.summaryCol}>
                      <span className={Styles.platformIconSmall} style={{ color: platform?.color }}>
                        {Icon && <Icon />}
                      </span>
                      <span>{platform?.name}</span>
                    </div>
                    <div className={Styles.summaryCol}>
                      {profile?.username ? `@${profile.username}` :
                       profile?.custom_url ? `@${profile.custom_url.replace('@', '')}` :
                       profile?.name || profile?.title || '-'}
                    </div>
                    <div className={Styles.summaryCol}>
                      {(() => {
                        const val = profile?.metrics?.followers ||
                          profile?.metrics?.follower_count ||
                          profile?.follower_count ||
                          profile?.subscriber_count ||
                          profile?.followers ||
                          profile?.followers_count ||
                          0;
                        if (val === 0) return '-';
                        if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                        if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
                        return val.toLocaleString();
                      })()}
                    </div>
                    <div className={Styles.summaryCol}>
                      {postCount > 0 ? postCount : '-'}
                    </div>
                    <div className={Styles.summaryCol}>
                      {freqStats?.frequency?.posts_per_week || freqStats?.posts_per_week || '-'}
                    </div>
                    <div className={Styles.summaryCol}>
                      {getEngagement()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Platform Tabs */}
        {selectedPlatforms.length > 0 && (
          <div className={Styles.section}>
            <div className={Styles.tabsContainer}>
              {selectedPlatforms.map((platformId) => {
                const platform = SOCIAL_PLATFORMS.find((p) => p.id === platformId);
                const Icon = platform?.icon;
                const platformData = getPlatformData(platformId);
                const hasData = platformData.profile || platformData.posts?.length > 0;

                return (
                  <button
                    key={platformId}
                    className={`${Styles.tab} ${activeTab === platformId ? Styles.activeTab : ''} ${hasData ? Styles.hasData : ''}`}
                    onClick={() => setActiveTab(platformId)}
                    data-platform={platformId}
                  >
                    <Icon style={{ color: activeTab === platformId ? '#fff' : platform?.color }} />
                    <span>{platform?.name}</span>
                    {hasData && <FiCheck className={Styles.tabCheck} />}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            {activeTab && (
              <div className={Styles.tabContent}>
                {(() => {
                  const platform = SOCIAL_PLATFORMS.find((p) => p.id === activeTab);
                  const Icon = platform?.icon;
                  const channel = socialChannels.find((ch) => ch.platform === activeTab);
                  const platformData = getPlatformData(activeTab);

                  return (
                    <>
                      <div className={Styles.channelHeader}>
                        {Icon && <Icon className={Styles.channelIcon} style={{ color: platform?.color }} />}
                        <span className={Styles.channelName}>{platform?.name}</span>
                      </div>

                      <div className={Styles.inputGroup}>
                        <label className={Styles.inputLabel}>Profile URL / Username</label>
                        <div className={Styles.inputWithButton}>
                          <input
                            type="text"
                            className={Styles.input}
                            placeholder={`Your ${platform?.name} ${activeTab === 'youtube' ? 'channel URL' : 'profile URL or @username'}`}
                            value={channelUrls[activeTab] || ''}
                            onChange={(e) => handleUrlChange(activeTab, e.target.value)}
                          />
                          {activeTab !== 'facebook' && (
                            <>
                              <button
                                type="button"
                                className={`${Styles.fetchButton} ${platformData.posts?.length > 0 ? Styles.refetchButton : ''}`}
                                onClick={platformData.handleFetch}
                                disabled={platformData.isFetching || !channelUrls[activeTab]}
                                data-platform={activeTab}
                              >
                                <FiDownload />
                                {platformData.isFetching ? 'Fetching...' :
                                 platformData.posts?.length > 0 ? 'Refresh' : 'Fetch Content'}
                              </button>
                              {platformData.posts?.length > 0 && (
                                <button
                                  type="button"
                                  className={Styles.removeChannelBtn}
                                  onClick={() => {
                                    setPlatformToRemove(activeTab);
                                    setShowRemoveModal(true);
                                  }}
                                  disabled={isRemoving}
                                  title="Remove channel and its content"
                                >
                                  <FiTrash2 />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div className={Styles.inputGroup}>
                        <label className={Styles.inputLabel}>Channel Purpose</label>
                        <textarea
                          className={Styles.textarea}
                          placeholder={`What do you use ${platform?.name} for? (e.g., brand awareness, community engagement, thought leadership)`}
                          value={channelPurposes[activeTab] || ''}
                          onChange={(e) => handlePurposeChange(activeTab, e.target.value)}
                          rows={2}
                        />
                      </div>

                      {/* Profile Card */}
                      {platformData.profile && (
                        <div className={Styles.profileCard} data-platform={activeTab}>
                          <div className={Styles.profileMain}>
                            {/* Profile Picture */}
                            <div className={Styles.profileAvatarPlaceholder}>
                              {(() => {
                                // Prefer stored URLs (permanent) over CDN URLs (expire after ~1 hour)
                                const profilePic = platformData.profile.stored_profile_pic_url ||
                                  platformData.profile.profile_pic_url ||
                                  platformData.profile.profile_image_url ||
                                  platformData.profile.avatar_url ||
                                  platformData.profile.thumbnail_url ||
                                  platformData.profile.picture ||
                                  platformData.profile.photo;

                                console.log('Profile pic URL for', activeTab, ':', profilePic);

                                if (profilePic) {
                                  return (
                                    <img
                                      src={profilePic}
                                      alt=""
                                      referrerPolicy="no-referrer"
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                                      onError={(e) => {
                                        console.log('Profile image failed to load:', profilePic);
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  );
                                }
                                return null;
                              })()}
                              <Icon style={{ color: platform?.color, display: platformData.profile.profile_pic_url ? 'none' : 'flex' }} />
                            </div>
                            <div className={Styles.profileDetails}>
                              <div className={Styles.profileNameRow}>
                                <div className={Styles.profileName}>
                                  {platformData.profile.name || platformData.profile.title || platformData.profile.full_name || platformData.profile.display_name}
                                </div>
                                {platformData.profile.verified && (
                                  <div className={Styles.verifiedBadge}>✓</div>
                                )}
                              </div>
                              <div className={Styles.profileUsername}>
                                {platformData.profile.username ? `@${platformData.profile.username}` :
                                 platformData.profile.handle ? `@${platformData.profile.handle}` :
                                 platformData.profile.screen_name ? `@${platformData.profile.screen_name}` : ''}
                              </div>
                              {(platformData.profile.bio || platformData.profile.description || platformData.profile.headline || platformData.profile.summary) && (
                                <div className={Styles.profileBio}>
                                  {platformData.profile.bio || platformData.profile.description || platformData.profile.headline || platformData.profile.summary}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Profile Metrics Grid */}
                          <div className={Styles.profileMetricsGrid}>
                            {/* Followers/Subscribers - all platforms */}
                            <div className={Styles.metricCard}>
                              <div className={Styles.metricValue}>
                                {(() => {
                                  const val = platformData.profile.metrics?.followers ||
                                    platformData.profile.metrics?.follower_count ||
                                    platformData.profile.follower_count ||
                                    platformData.profile.subscriber_count ||
                                    platformData.profile.followers ||
                                    platformData.profile.followers_count ||
                                    0;
                                  // For company pages without follower data, show N/A
                                  if (val === 0 && platformData.profile.is_company) return 'N/A';
                                  if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                                  if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
                                  return val.toLocaleString();
                                })()}
                              </div>
                              <div className={Styles.metricLabel}>
                                {activeTab === 'youtube' ? 'Subscribers' : 'Followers'}
                              </div>
                            </div>

                            {/* Following - Twitter, Instagram */}
                            {(activeTab === 'twitter' || activeTab === 'instagram') && (
                              <div className={Styles.metricCard}>
                                <div className={Styles.metricValue}>
                                  {(() => {
                                    const val = platformData.profile.metrics?.following ||
                                      platformData.profile.following ||
                                      platformData.profile.friends_count ||
                                      0;
                                    if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                                    if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
                                    return val.toLocaleString();
                                  })()}
                                </div>
                                <div className={Styles.metricLabel}>Following</div>
                              </div>
                            )}

                            {/* Posts/Tweets/Videos count */}
                            <div className={Styles.metricCard}>
                              <div className={Styles.metricValue}>
                                {(() => {
                                  const val = platformData.profile.metrics?.tweet_count ||
                                    platformData.profile.metrics?.media_count ||
                                    platformData.profile.media_count ||
                                    platformData.profile.posts_count ||
                                    platformData.profile.video_count ||
                                    platformData.profile.statuses_count ||
                                    platformData.profile.post_count ||
                                    platformData.posts?.length ||
                                    platformData.frequencyStats?.sample_size ||
                                    0;
                                  if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                                  if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
                                  return val.toLocaleString();
                                })()}
                              </div>
                              <div className={Styles.metricLabel}>
                                {activeTab === 'twitter' ? 'Tweets' :
                                 activeTab === 'youtube' || activeTab === 'tiktok' ? 'Videos' : 'Posts'}
                              </div>
                            </div>

                            {/* LinkedIn Connections */}
                            {activeTab === 'linkedin' && (
                              <div className={Styles.metricCard}>
                                <div className={Styles.metricValue}>
                                  {(() => {
                                    const val = platformData.profile.connections ||
                                      platformData.profile.metrics?.connections ||
                                      platformData.profile.network_size ||
                                      '500+';
                                    if (typeof val === 'number') {
                                      if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                                      if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
                                      return val.toLocaleString();
                                    }
                                    return val;
                                  })()}
                                </div>
                                <div className={Styles.metricLabel}>Connections</div>
                              </div>
                            )}

                            {/* YouTube Total Views */}
                            {activeTab === 'youtube' && platformData.profile.view_count && (
                              <div className={Styles.metricCard}>
                                <div className={Styles.metricValue}>
                                  {(() => {
                                    const val = platformData.profile.view_count || 0;
                                    if (val >= 1000000000) return (val / 1000000000).toFixed(1) + 'B';
                                    if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                                    if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
                                    return val.toLocaleString();
                                  })()}
                                </div>
                                <div className={Styles.metricLabel}>Total Views</div>
                              </div>
                            )}

                            {/* TikTok Total Likes */}
                            {activeTab === 'tiktok' && (
                              <div className={Styles.metricCard}>
                                <div className={Styles.metricValue}>
                                  {(() => {
                                    const val = platformData.profile.likes ||
                                      platformData.profile.heart_count ||
                                      platformData.profile.total_likes ||
                                      0;
                                    if (val >= 1000000000) return (val / 1000000000).toFixed(1) + 'B';
                                    if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                                    if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
                                    return val.toLocaleString();
                                  })()}
                                </div>
                                <div className={Styles.metricLabel}>Total Likes</div>
                              </div>
                            )}
                          </div>

                          {/* Profile Link */}
                          {(platformData.profile.url || platformData.profile.profile_url || channelUrls[activeTab]) && (
                            <a
                              href={platformData.profile.url || platformData.profile.profile_url || channelUrls[activeTab]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={Styles.profileLink}
                            >
                              <FiExternalLink /> View Full Profile on {platform?.name}
                            </a>
                          )}
                        </div>
                      )}

                      {/* Frequency Stats */}
                      {platformData.frequencyStats && (
                        <div className={Styles.statsGrid}>
                          <div className={Styles.statCard}>
                            <div className={Styles.statValue}>
                              {platformData.frequencyStats.frequency?.posts_per_week ||
                                platformData.frequencyStats.posts_per_week ||
                                '-'}
                            </div>
                            <div className={Styles.statLabel}>Posts/Week</div>
                          </div>
                          <div className={Styles.statCard}>
                            <div className={Styles.statValue}>
                              {platformData.frequencyStats.frequency?.estimated_monthly ||
                                platformData.frequencyStats.estimated_monthly ||
                                '-'}
                            </div>
                            <div className={Styles.statLabel}>Est. Monthly</div>
                          </div>
                          {platformData.frequencyStats.patterns?.most_active_day && (
                            <div className={Styles.statCard}>
                              <div className={Styles.statValue}>
                                {platformData.frequencyStats.patterns.most_active_day}
                              </div>
                              <div className={Styles.statLabel}>Most Active Day</div>
                            </div>
                          )}
                          {platformData.frequencyStats.patterns?.most_active_hour && (
                            <div className={Styles.statCard}>
                              <div className={Styles.statValue}>
                                {platformData.frequencyStats.patterns.most_active_hour}:00
                              </div>
                              <div className={Styles.statLabel}>Most Active Hour</div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Content Insights Section (Collapsible) */}
                      {platformData.posts?.length > 0 && brandId && (
                        <div className={Styles.insightsSection}>
                          <button
                            className={`${Styles.insightsToggle} ${expandedInsights[activeTab] ? Styles.expanded : ''}`}
                            onClick={() => toggleInsights(activeTab)}
                          >
                            <div className={Styles.insightsToggleLeft}>
                              <FiBarChart2 />
                              <span>Content Analysis</span>
                              {contentInsights[activeTab] && !isAnalyzing[activeTab] && (
                                <span className={Styles.insightsReady}>
                                  <FiCheck /> Ready
                                </span>
                              )}
                              {isAnalyzing[activeTab] && (
                                <span className={Styles.insightsLoading}>
                                  <FiRefreshCw className={Styles.spinning} /> Analyzing...
                                </span>
                              )}
                            </div>
                            <div className={Styles.insightsToggleRight}>
                              {expandedInsights[activeTab] ? <FiChevronUp /> : <FiChevronDown />}
                            </div>
                          </button>

                          {expandedInsights[activeTab] && (
                            <div className={Styles.insightsContent}>
                              {isAnalyzing[activeTab] && !contentInsights[activeTab] ? (
                                <div className={Styles.insightsLoaderContainer}>
                                  <FiRefreshCw className={Styles.spinning} />
                                  <p>Analyzing your content for patterns, top performers, and insights...</p>
                                </div>
                              ) : contentInsights[activeTab] ? (
                                <>
                                  {/* Content Pillars */}
                                  {contentInsights[activeTab].content_pillars?.length > 0 && (
                                    <div className={Styles.insightsCard}>
                                      <div className={Styles.insightsCardHeader}>
                                        <FiTarget />
                                        <h4>Content Pillars</h4>
                                      </div>
                                      <div className={Styles.pillarsGrid}>
                                        {contentInsights[activeTab].content_pillars.slice(0, 5).map((pillar, idx) => (
                                          <div key={idx} className={Styles.pillarItem}>
                                            <div className={Styles.pillarBar}>
                                              <div
                                                className={Styles.pillarFill}
                                                style={{ width: `${pillar.percentage || 0}%` }}
                                              />
                                            </div>
                                            <div className={Styles.pillarInfo}>
                                              <span className={Styles.pillarName}>{pillar.name}</span>
                                              <span className={Styles.pillarPercent}>{pillar.percentage || 0}%</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Top Performers */}
                                  {contentInsights[activeTab].top_performers?.length > 0 && (
                                    <div className={Styles.insightsCard}>
                                      <div className={Styles.insightsCardHeader}>
                                        <FiTrendingUp />
                                        <h4>Top Performing Content</h4>
                                      </div>
                                      <div className={Styles.topPerformersList}>
                                        {contentInsights[activeTab].top_performers.slice(0, 3).map((post, idx) => (
                                          <div key={idx} className={Styles.topPerformerItem}>
                                            <div className={Styles.performerRank}>#{idx + 1}</div>
                                            <div className={Styles.performerContent}>
                                              <p className={Styles.performerPreview}>
                                                {post.content_preview || 'No preview available'}
                                              </p>
                                              <div className={Styles.performerMeta}>
                                                <span className={Styles.engagementRate}>
                                                  {post.engagement_rate}% engagement
                                                </span>
                                              </div>
                                              {post.why_it_worked?.length > 0 && (
                                                <div className={Styles.whyItWorked}>
                                                  <strong>Why it worked:</strong>
                                                  <ul>
                                                    {post.why_it_worked.slice(0, 3).map((reason, rIdx) => (
                                                      <li key={rIdx}>{reason}</li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Recommendations */}
                                  {contentInsights[activeTab].recommendations?.length > 0 && (
                                    <div className={Styles.insightsCard}>
                                      <div className={Styles.insightsCardHeader}>
                                        <FiZap />
                                        <h4>AI Recommendations</h4>
                                      </div>
                                      <ul className={Styles.recommendationsList}>
                                        {contentInsights[activeTab].recommendations.map((rec, idx) => (
                                          <li key={idx}>{rec}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Refresh button */}
                                  <button
                                    className={Styles.refreshAnalysisBtn}
                                    onClick={() => analyzeContentInsights(activeTab, true)}
                                    disabled={isAnalyzing[activeTab]}
                                  >
                                    <FiRefreshCw className={isAnalyzing[activeTab] ? Styles.spinning : ''} />
                                    {isAnalyzing[activeTab] ? 'Analyzing...' : 'Refresh Analysis'}
                                  </button>
                                </>
                              ) : (
                                <div className={Styles.insightsEmpty}>
                                  <p>No analysis available yet.</p>
                                  <button
                                    className={Styles.runAnalysisBtn}
                                    onClick={() => analyzeContentInsights(activeTab, true)}
                                    disabled={isAnalyzing[activeTab]}
                                  >
                                    <FiBarChart2 />
                                    Run Content Analysis
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Posts List */}
                      {platformData.posts?.length > 0 && (
                        <div className={Styles.postsSection}>
                          <div className={Styles.postsHeader}>
                            <span>Fetched Content ({platformData.posts.length})</span>
                            {platformData.posts.length > (activeTab === 'instagram' || activeTab === 'tiktok' ? 9 : 5) && (
                              <button
                                className={Styles.seeMoreBtn}
                                onClick={() => setShowAllPosts(prev => ({ ...prev, [activeTab]: !prev[activeTab] }))}
                              >
                                {showAllPosts[activeTab] ? (
                                  <>
                                    <FiChevronUp /> See Less
                                  </>
                                ) : (
                                  <>
                                    <FiChevronDown /> See All
                                  </>
                                )}
                              </button>
                            )}
                          </div>

                          {/* Visual Grid for Instagram, TikTok & YouTube */}
                          {(activeTab === 'instagram' || activeTab === 'tiktok' || activeTab === 'youtube') ? (
                            <div className={Styles.visualPostsGrid}>
                              {(showAllPosts[activeTab] ? platformData.posts : platformData.posts.slice(0, 9)).map((post, index) => {
                                // Prefer stored URLs (permanent) over CDN URLs (expire after ~1 hour)
                                const thumbnail = post.stored_thumbnail_url ||
                                  post.thumbnail_url ||
                                  post.thumbnail ||
                                  post.image_url ||
                                  post.media_url ||
                                  post.display_url ||
                                  post.thumbnail_src ||
                                  post.cover ||
                                  post.cover_url ||
                                  post.video_url ||
                                  post.preview ||
                                  post.preview_url ||
                                  post.picture ||
                                  post.photo ||
                                  post.video_thumbnail ||
                                  post.thumbnail_medium ||
                                  (post.images && post.images[0]?.url) ||
                                  (post.media && post.media[0]?.url) ||
                                  (post.carousel_media && post.carousel_media[0]?.image_versions2?.candidates?.[0]?.url);
                                const postUrl = post.url || post.permalink || post.link ||
                                  (post.shortcode ? `https://instagram.com/p/${post.shortcode}` : null) ||
                                  (post.video_id ? `https://youtube.com/watch?v=${post.video_id}` : null);

                                // Get platform icon for no-thumbnail state
                                const getPlatformIcon = () => {
                                  if (activeTab === 'instagram') return <FiInstagram />;
                                  if (activeTab === 'youtube') return <FiYoutube />;
                                  return <FaTiktok />;
                                };

                                return (
                                  <div key={`${activeTab}-${post.id || index}`} className={Styles.visualPostCard}>
                                    <div className={Styles.visualPostMedia}>
                                      {index === 0 && console.log('First post thumbnail URL:', thumbnail, 'Full post:', post)}
                                      {thumbnail && (
                                        <img
                                          src={thumbnail}
                                          alt=""
                                          referrerPolicy="no-referrer"
                                          onError={(e) => {
                                            console.log('Thumbnail failed to load:', thumbnail);
                                            e.target.style.display = 'none';
                                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                      )}
                                      <div className={Styles.noThumbnail} style={{ display: thumbnail ? 'none' : 'flex' }}>
                                        {getPlatformIcon()}
                                      </div>
                                      {(activeTab === 'tiktok' || activeTab === 'youtube') && (
                                        <div className={Styles.videoIndicator}>
                                          <FiPlay />
                                        </div>
                                      )}
                                      {/* Hover overlay with metrics */}
                                      <div className={Styles.visualPostOverlay}>
                                        <div className={Styles.overlayMetrics}>
                                          {post.likes !== undefined && (
                                            <span>❤️ {post.likes >= 1000 ? (post.likes/1000).toFixed(1) + 'K' : post.likes}</span>
                                          )}
                                          {post.comments !== undefined && (
                                            <span>💬 {post.comments >= 1000 ? (post.comments/1000).toFixed(1) + 'K' : post.comments}</span>
                                          )}
                                          {post.views !== undefined && (
                                            <span>👁️ {post.views >= 1000000 ? (post.views/1000000).toFixed(1) + 'M' : post.views >= 1000 ? (post.views/1000).toFixed(1) + 'K' : post.views}</span>
                                          )}
                                        </div>
                                        {postUrl && (
                                          <a
                                            href={postUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={Styles.overlayLink}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <FiExternalLink />
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                    {(post.caption || post.title || post.description) && (
                                      <div className={Styles.visualPostCaption}>
                                        {post.caption || post.title || post.description}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                          /* Standard List for Twitter, LinkedIn, YouTube */
                          <div className={Styles.postsList}>
                            {(showAllPosts[activeTab] ? platformData.posts : platformData.posts.slice(0, 5))
                              .sort((a, b) => {
                                // Sort by date - newest first
                                const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                                const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                                return dateB - dateA;
                              })
                              .map((post, index) => {
                              const postId = `${activeTab}-${post.id || index}`;
                              const isExpanded = expandedPosts[postId];
                              // Prefer stored URLs (permanent) over CDN URLs
                              const hasThumbnail = post.stored_thumbnail_url || post.thumbnail || post.thumbnail_url || post.image_url || post.media_url;
                              const isVisualPlatform = activeTab === 'instagram' || activeTab === 'tiktok' || activeTab === 'youtube';

                              // Get platform-specific content text
                              const getPostText = () => {
                                if (activeTab === 'twitter') return post.text || '';
                                if (activeTab === 'instagram') return post.caption || '';
                                if (activeTab === 'linkedin') return post.text || post.content || '';
                                if (activeTab === 'youtube') return post.title || '';
                                if (activeTab === 'tiktok') return post.title || post.description || '';
                                return post.title || post.text || post.caption || post.content || '';
                              };

                              // Format large numbers
                              const formatNumber = (num) => {
                                if (num === undefined || num === null) return '0';
                                if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
                                if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
                                return num.toLocaleString();
                              };

                              return (
                                <div
                                  key={postId}
                                  className={`${Styles.postItem} ${isVisualPlatform ? Styles.visualPost : ''}`}
                                  data-platform={activeTab}
                                >
                                  {/* Thumbnail - Large for visual platforms */}
                                  {hasThumbnail && isVisualPlatform && (
                                    <div className={Styles.postMediaLarge}>
                                      <img
                                        src={post.stored_thumbnail_url || post.thumbnail || post.thumbnail_url || post.image_url || post.media_url}
                                        alt=""
                                        onError={(e) => {
                                          e.target.parentElement.style.display = 'none';
                                        }}
                                      />
                                      {(activeTab === 'youtube' || activeTab === 'tiktok') && (
                                        <div className={Styles.playOverlayLarge}>
                                          <FiPlay />
                                        </div>
                                      )}
                                      {/* Engagement overlay on image */}
                                      <div className={Styles.engagementOverlay}>
                                        {post.views !== undefined && (
                                          <span className={Styles.engagementBadge}>
                                            <FiPlay /> {formatNumber(post.views)}
                                          </span>
                                        )}
                                        {post.likes !== undefined && (
                                          <span className={Styles.engagementBadge}>
                                            ❤️ {formatNumber(post.likes)}
                                          </span>
                                        )}
                                        {post.comments !== undefined && (
                                          <span className={Styles.engagementBadge}>
                                            💬 {formatNumber(post.comments)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  <div className={Styles.postBody}>
                                    <div
                                      className={Styles.postPreview}
                                      onClick={() => togglePostExpanded(postId)}
                                    >
                                      {/* Small thumbnail for non-visual platforms */}
                                      {hasThumbnail && !isVisualPlatform && (
                                        <div className={Styles.postThumbnail}>
                                          <img
                                            src={post.stored_thumbnail_url || post.thumbnail || post.thumbnail_url || post.image_url || post.media_url}
                                            alt=""
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        </div>
                                      )}
                                      <div className={Styles.postContent}>
                                        <div className={`${Styles.postText} ${isExpanded ? Styles.postTextExpanded : ''}`}>
                                          {getPostText()}
                                        </div>
                                        {/* Metrics row for non-visual or no-thumbnail posts */}
                                        {(!isVisualPlatform || !hasThumbnail) && (
                                          <div className={Styles.metricsRow}>
                                            {activeTab === 'twitter' && (
                                              <>
                                                {post.likes !== undefined && (
                                                  <span className={Styles.metricBadge}><span className={Styles.metricIcon}>❤️</span> {formatNumber(post.likes)}</span>
                                                )}
                                                {post.retweets !== undefined && (
                                                  <span className={Styles.metricBadge}><span className={Styles.metricIcon}>🔁</span> {formatNumber(post.retweets)}</span>
                                                )}
                                                {post.replies !== undefined && (
                                                  <span className={Styles.metricBadge}><span className={Styles.metricIcon}>💬</span> {formatNumber(post.replies)}</span>
                                                )}
                                              </>
                                            )}
                                            {activeTab === 'linkedin' && (
                                              <>
                                                {post.likes !== undefined && (
                                                  <span className={Styles.metricBadge}><span className={Styles.metricIcon}>👍</span> {formatNumber(post.likes)}</span>
                                                )}
                                                {post.comments !== undefined && (
                                                  <span className={Styles.metricBadge}><span className={Styles.metricIcon}>💬</span> {formatNumber(post.comments)}</span>
                                                )}
                                              </>
                                            )}
                                            {post.created_at && (
                                              <span className={Styles.postDate}>{new Date(post.created_at).toLocaleDateString()}</span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <button className={Styles.expandBtn}>
                                        {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                                      </button>
                                    </div>

                                    {/* Footer with date and view link */}
                                    <div className={Styles.postFooter}>
                                      {post.created_at && isVisualPlatform && (
                                        <span className={Styles.postDate}>
                                          {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                      )}
                                      {post.url && (
                                        <a
                                          href={post.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={Styles.viewOnPlatform}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <FiExternalLink /> View on {platform?.name}
                                        </a>
                                      )}
                                    </div>

                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}


        {/* Posting Frequency */}
        <div className={Styles.inputGroup}>
          <label className={Styles.label}>Overall Posting Frequency</label>
          <textarea
            className={Styles.textarea}
            placeholder="How often do you post across your channels? (e.g., daily, 3x per week, varies by platform)"
            value={postingFrequency}
            onChange={(e) => onUpdate({ postingFrequency: e.target.value })}
            rows={2}
          />
        </div>

        {/* Info Box */}
        <div className={Styles.infoBox}>
          <div className={Styles.infoTitle}>Platform-Specific Content</div>
          <div className={Styles.infoText}>
            AI will adapt your content style and format for each platform's best practices
            and audience expectations.
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={Styles.footer}>
        <button className={Styles.previousBtn} onClick={onPrevious}>
          Previous
        </button>
        <button className={Styles.saveExitBtn} onClick={onSaveAndExit}>
          Save & Exit
        </button>
        <div className={Styles.stepIndicator}>Step 4 of 7</div>
        <button className={Styles.continueBtn} onClick={handleContinue}>
          Save & Continue
        </button>
      </div>

      {/* Remove Channel Confirmation Modal */}
      <ConfirmDeleteModal
        show={showRemoveModal}
        onHide={() => {
          setShowRemoveModal(false);
          setPlatformToRemove(null);
        }}
        onDelete={handleRemoveChannel}
        modalHeading="Remove Channel"
        modalDescription={`Are you sure you want to remove this ${SOCIAL_PLATFORMS.find(p => p.id === platformToRemove)?.name || ''} channel? This will also remove all fetched content samples from your brand training data. This action cannot be undone.`}
        actionText={isRemoving ? 'Removing...' : 'Remove Channel'}
      />
    </div>
  );
};

export default Step6SocialChannels;
